import React, { useState } from "react";
import { DateTime } from "luxon";
import { formatImageUrl } from "@/lib/general";
import { FormData } from "./types";
import { Currency } from "@/types/currency";
import placeholderImage from "@/assets/imgloading.png";

interface PreviewCardProps {
  formData: FormData;
  selectedCurrency: Currency;
  timeZone?: string;
}

const PreviewCard: React.FC<PreviewCardProps> = ({
  formData,
  selectedCurrency,
  timeZone = "UTC",
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(true);

  const {
    name,
    pricePerNight,
    totalPrice,
    priceType,
    numGuests,
    numNights,
    checkInTime,
    checkOutTime,
    amenities = [],
    images,
  } = formData;

  return (
    <div className="flex gap-4 h-full">
      {/* Left column - Image preview */}
      <div className="w-[40%] h-full">
        <div className="flex flex-col h-full">
          <div className="relative h-full w-full">
            <div className="w-full h-full rounded-lg overflow-hidden">
              {images.length > 0 && images[currentImageIndex] ? (
                <div className="relative w-full h-full">
                  <img
                    src={formatImageUrl(
                      images[currentImageIndex].url ||
                        images[currentImageIndex].preview,
                      "high",
                      false
                    )}
                    alt={name || "Property preview"}
                    className={`w-full h-full object-cover transition-opacity duration-300 ${
                      isImageLoading ? "opacity-0" : "opacity-100"
                    }`}
                    onLoad={() => setIsImageLoading(false)}
                  />
                  {isImageLoading && (
                    <div className="absolute inset-0 bg-neutral-100 flex items-center justify-center">
                      <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full h-full bg-neutral-100 flex items-center justify-center">
                  <img
                    src={placeholderImage}
                    alt={name || "Property preview"}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right column - Details */}
      <div className="flex-1 min-w-0 flex flex-col min-h-[8rem] w-full">
        <div className="w-full h-full flex flex-col">
          <div className="flex items-center flex-wrap gap-2">
            <h2 className="text-base 2xl:text-lg font-medium text-neutral-800 truncate max-w-[calc(100%-8rem)]">
              {name || <span className="opacity-70">Your Property Name</span>}
            </h2>
          </div>

          {/* Info boxes */}
          <div className="flex gap-3 mt-1.5 mb-2">
            {/* Price per night */}
            <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-md 2xl:px-2 2xl:py-1.5">
              <i className="far fa-money-bill text-neutral-700 text-xs 2xl:text-[13px]" />
              <span className="text-[11px] text-neutral-700 font-light 2xl:text-[13px]">
                {priceType === "perNight" ? (
                  pricePerNight ? (
                    `${selectedCurrency.symbol}${pricePerNight}/night`
                  ) : (
                    <span className="opacity-70">
                      {selectedCurrency.symbol}100/night
                    </span>
                  )
                ) : totalPrice ? (
                  `${selectedCurrency.symbol}${totalPrice} total`
                ) : (
                  <span className="opacity-70">
                    {selectedCurrency.symbol}500 total
                  </span>
                )}
              </span>
            </div>

            {/* Number of guests */}
            {numGuests && (
              <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-md">
                <i className="far fa-users text-neutral-700 text-xs" />
                <span className="text-[11px] text-neutral-700 font-light">
                  {numGuests} guests
                </span>
              </div>
            )}

            {/* Number of nights */}
            {numNights && (
              <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-md">
                <i className="far fa-moon text-neutral-700 text-xs" />
                <span className="text-[11px] text-neutral-700 font-light">
                  {numNights} nights
                </span>
              </div>
            )}
          </div>

          {/* Check-in/out times */}
          <div className="mt-1 space-y-1">
            <div className="flex items-center gap-2">
              <i className="far fa-clock text-neutral-700 text-xs 2xl:text-[13px]" />
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] 2xl:text-[13px] text-neutral-500">
                  Check-in:
                </span>
                <span className="text-[11px] 2xl:text-[13px] text-neutral-700 font-light">
                  {checkInTime ? (
                    DateTime.fromISO(checkInTime)
                      .setZone(timeZone)
                      .toFormat("h:mm a")
                  ) : (
                    <span className="opacity-70">3:00 PM</span>
                  )}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <i className="far fa-clock text-neutral-700 text-xs 2xl:text-[13px]" />
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] 2xl:text-[13px] text-neutral-500">
                  Check-out:
                </span>
                <span className="text-[11px] 2xl:text-[13px] text-neutral-700 font-light">
                  {checkOutTime ? (
                    DateTime.fromISO(checkOutTime)
                      .setZone(timeZone)
                      .toFormat("h:mm a")
                  ) : (
                    <span className="opacity-70">11:00 AM</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Amenities */}
          <div className="flex flex-row gap-2 mt-auto overflow-x-auto no-scrollbar">
            {amenities.map((amenity, index) => (
              <div
                key={index}
                className="flex items-center gap-2 bg-slate-50 px-2 py-1 rounded-md whitespace-nowrap"
              >
                <i className="far fa-check text-xs text-neutral-700" />
                <span className="text-[11px] text-neutral-700 font-light">
                  {amenity}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewCard;
